import { all, call, select, takeEvery, put } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { closeModal, waitForModal } from 'components/Modal';
import { subDays } from 'date-fns';

import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import { ACTIVE_VENUE_CHANGED, waitForAppInit } from 'features/App/App.saga';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { trackCampaignLaunch } from 'helpers/events';
import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenueId, selectIaLogic, selectRouteParams } from 'selectors/general';
import { campaigns as campaignsEntity } from 'store/saga/entities/bound';

import { selectCampaigns } from './Campaigns.selectors';
import messages from './Campaigns.messages';
import { refreshCampaignCacheForVenueId } from './V2';

export const addCampaign = (formValues, formAsyncFns) => ({
  type: 'CAMPAIGNS/ADD_CAMPAIGN',
  formValues,
  formAsyncFns,
});
export const editCampaign = (formValues, formAsyncFns) => ({
  type: 'CAMPAIGNS/EDIT_CAMPAIGN',
  formValues,
  formAsyncFns,
});

export const endCampaign = formValues => ({
  type: 'CAMPAIGNS/END_CAMPAIGN',
  formValues,
});

export const openLearnMoreModal = () => ({
  type: 'CAMPAIGNS/OPEN_LEARN_MORE_MODAL',
});

export function* doAddCampaign(client, action) {
  const { formValues, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;
  const { campaignType, startDate, endDate, venueId } = formValues;

  let promotions = [];

  try {
    yield call(fetchEntity, campaignsEntity, [client, campaignsEntity.method.create, formValues]);
  } catch (error) {
    reportError('Failed to create Campaign', {
      originalError: error,
    });

    yield all([call(rejectSubmit), put(closeModal()), put(createErrorSnackbar(messages.saga.createError))]);
  }

  try {
    yield call(fetchEntity, campaignsEntity, [client, campaignsEntity.method.options, { venueId }]);
  } catch (error) {
    reportError('Failed to re-fetch Campaign options', {
      originalError: error,
    });

    yield all([call(rejectSubmit), put(closeModal()), put(createErrorSnackbar(messages.saga.createError))]);
  }

  try {
    yield call(fetchEntity, campaignsEntity, [client, campaignsEntity.method.read, { venueId }]);
  } catch (error) {
    reportError('Failed to re-fetch existing Campaigns', {
      originalError: error,
    });

    yield all([call(rejectSubmit), put(closeModal()), put(createErrorSnackbar(messages.saga.createError))]);
  }

  try {
    const { data } = yield select(selectCampaigns, venueId);
    promotions = data;
    yield call(trackCampaignLaunch, { type: campaignType, startDate, endDate, promotions });
  } catch (error) {
    reportError('Failed to track Campaign launch (Segment)', {
      originalError: error,
    });

    yield all([call(rejectSubmit), put(closeModal()), put(createErrorSnackbar(messages.saga.createError))]);
  }

  try {
    const newPath = makePath.campaigns({ venueId });
    yield all([
      call(resolveSubmit),
      put(closeModal()),
      put(createSuccessSnackbar(messages.saga.createSuccess)),
      call(client.navigate, newPath),
    ]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.CAMPAIGN_CREATED);
    yield call(refreshCampaignCacheForVenueId, venueId);
  } catch (error) {
    reportError('Failed to track Campaign launch (Braze)', {
      originalError: error,
    });

    yield all([call(rejectSubmit), put(closeModal()), put(createErrorSnackbar(messages.saga.createError))]);
  }
}

export function* doEditCampaign(client, action) {
  const { formValues, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;
  const { venueId } = formValues;

  try {
    yield call(fetchEntity, campaignsEntity, [client, campaignsEntity.method.update, formValues]);

    const newPath = makePath.campaigns({ venueId });
    yield all([
      call(resolveSubmit),
      put(closeModal()),
      put(createSuccessSnackbar(messages.saga.updateSuccess)),
      call(client.navigate, newPath),
    ]);
    yield call(refreshCampaignCacheForVenueId, venueId);
  } catch (error) {
    yield all([call(rejectSubmit), put(closeModal()), put(createErrorSnackbar(messages.saga.updateError))]);
  }
}

export function* doEndCampaign(client, action) {
  const { formValues } = action;
  const today = new Date();
  const endDate = subDays(today, 1);
  const { venueId, partnerCampaignId } = formValues;
  const newPath = makePath.campaigns({ venueId });

  try {
    yield call(fetchEntity, campaignsEntity, [
      client,
      campaignsEntity.method.update,
      {
        venueId,
        partnerCampaignId,
        endDate,
      },
    ]);
    yield all([
      put(closeModal()),
      put(createSuccessSnackbar(messages.saga.endSuccess)),
      call(client.navigate, newPath),
    ]);
    yield call(refreshCampaignCacheForVenueId, venueId);
  } catch (error) {
    yield put(createErrorSnackbar(messages.saga.updateError));
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  const activeVenueId = yield select(selectActiveVenueId);

  if (venueId !== activeVenueId) {
    yield all([
      call(fetchEntity, campaignsEntity, [client, campaignsEntity.method.read, { venueId }]),
      call(fetchEntity, campaignsEntity, [client, campaignsEntity.method.options, { venueId }]),
    ]);

    const newPath = makePath.campaigns({ venueId });
    yield call(client.navigate, newPath);
  }
}

export function* doOpenLearnMoreModal(client) {
  yield call(waitForModal, client, 'CampaignsModal', {
    className: 'slideshow-modal__modal-content',
  });
}

export function* handleRouteEnter(client) {
  const venueId = yield select(selectActiveVenueId);

  yield all([
    call(fetchEntityIfNeeded, campaignsEntity, [client, campaignsEntity.method.read, { venueId }]),
    call(fetchEntityIfNeeded, campaignsEntity, [client, campaignsEntity.method.options, { venueId }]),
  ]);
}

export function* handleRootRouteEnter(client) {
  yield call(waitForAppInit);

  const venueId = yield select(selectActiveVenueId);
  const { userHasAccess } = yield select(selectIaLogic, IA_KEY.CAMPAIGNS);

  let newPath;
  if (userHasAccess) {
    newPath = makePath.campaigns({ venueId });
  }

  if (newPath) {
    yield call(client.navigate, newPath);
  }
}

export function* handleEditRouteEnter(client) {
  const { venueId, partnerCampaignId } = yield select(selectRouteParams);
  yield all([
    call(fetchEntity, campaignsEntity, [
      client,
      campaignsEntity.method.getOne,
      {
        venueId,
        partnerCampaignId,
      },
    ]),
    call(fetchEntityIfNeeded, campaignsEntity, [client, campaignsEntity.method.options, { venueId }]),
  ]);
}

export function* watchActions(client) {
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(addCampaign().type, doAddCampaign, client);
  yield takeEvery(editCampaign().type, doEditCampaign, client);
  yield takeEvery(endCampaign().type, doEndCampaign, client);
  yield takeEvery(openLearnMoreModal().type, doOpenLearnMoreModal, client);
}
